import BookGrid from "@app/components/book-grid";
import {
  BannerAdsPosition,
  BookOption,
  BookOptionName,
  RoutePath,
  SystemMessage,
  USER_INFO_KEY,
} from "@app/constants";
import HeartIcon from "@app/icons/heart";
import BookService from "@app/services/http/book.service";
import {
  Book,
  BookDetail,
  BookElementChildrenDetail,
  BookElementDetail,
  BookMediaType,
} from "@app/types";
import { Images } from "@assets/images";
import clsx from "clsx";
import { useCallback, useEffect, useMemo, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ImageGallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";
import { useDispatch, useSelector } from "react-redux";
import { selectAuth, storeUser } from "@app/store/auth";
import {
  AudiobookData,
  AudioItem,
  clearAudiobook,
  selectAudiobook,
  sendAudiobook,
  showAudiobookPanel,
} from "@app/store/audiobook";
import { addToast } from "@app/components/toast/toast.service";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import BannerAdsComponent from "../home/components/banner-ads";
import { formatMoneyVN } from "@app/utils/util";
import { LuLockKeyhole } from "react-icons/lu";
import { LuLockKeyholeOpen } from "react-icons/lu";
import UnlockBookElementModal from "@app/components/unlock-book-element-modal/unlock-book-element-modal.component";
import UserService from "@app/services/http/user.service";
import StorageService from "@core/services/storage";
import { FaLongArrowAltRight } from "react-icons/fa";
import DeepLinkService from "@app/services/http/deeplink.service";
import { useCopyToClipboard } from "@core/hooks/use-copy-to-clipboard.hook";
import PurchaseService from "@app/services/http/purchase.service";
import { openPortalDialog } from "@app/services/modal.service";
import PurchaseMediaConfirmModal from "@app/components/purchase-media-confirm-modal";

const getDisplayAuthor = (book?: BookDetail) => {
  if (!book) {
    return "";
  }

  if (book.authors?.length) {
    return book.authors
      .map((item) => item.fullName)
      .filter((item) => !!item)
      .join(", ");
  }

  return book.author ?? "";
};

const getElementAudioLink = (item: BookElementDetail) => {
  if (item.linkAudio) {
    return item.linkAudio;
  }

  return item.audios?.find((audio) => !!audio.audio)?.audio ?? "";
};

type BookElementTreeItem = BookElementDetail | BookElementChildrenDetail;

const getElementChildren = (
  item: BookElementTreeItem
): BookElementChildrenDetail[] => item.child ?? [];

const flattenBookElements = (
  elements: BookElementDetail[] = []
): BookElementTreeItem[] => {
  const outputs: BookElementTreeItem[] = [];

  const traverse = (item: BookElementTreeItem) => {
    outputs.push(item);
    getElementChildren(item).forEach((child) => {
      traverse(child);
    });
  };

  elements.forEach((item) => {
    traverse(item);
  });

  return outputs;
};

function BookDetailPage() {
  const { id } = useParams();

  const [book, setBook] = useState<BookDetail>();
  const [bookCategoryFavorite, setBookCategoryFavorite] = useState<Book[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingRecommendations, setLoadingRecommendations] = useState(true);
  const [openSubmenus, setOpenSubmenus] = useState<Record<number, boolean>>({});

  const toggleSubmenu = (id: number) => {
    setOpenSubmenus((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const userInfo = useSelector(selectAuth);
  const audiobookState = useSelector(selectAudiobook);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { abortController } = useCancellationRequest();
  const [, copy] = useCopyToClipboard();

  useEffect(() => {
    if (!isNaN(Number(id))) {
      BookService.getBookById(Number(id), abortController.signal)
        .then((bookRes) => {
          setBook(bookRes.data);
          window.scrollTo({ top: 0, behavior: "smooth" });
        })
        .finally(() => {
          setLoading(false);
        });

      BookService.getBookCategoryFavorite({}, abortController.signal)
        .then((bookCategoryFavoriteRes) => {
          setBookCategoryFavorite(bookCategoryFavoriteRes.data.items);
        })
        .finally(() => {
          setLoadingRecommendations(false);
        });
    } else {
      navigate(RoutePath.HOME);
    }
  }, [id]);

  // Tự mở popup mua audio khi được redirect từ ebook-reading hoặc reading page
  useEffect(() => {
    if (!book || !location.state?.buyAudio) {
      return;
    }

    // Xóa state để không trigger lại khi navigate trong trang
    navigate(location.pathname, { replace: true, state: {} });
    handleListenBookClick();
  }, [book, location.state?.buyAudio]);

  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  const fetchBookDetail = () => {
    if (!isNaN(Number(id))) {
      BookService.getBookById(Number(id), undefined, {
        forceRefresh: true,
      }).then((res) => {
        setBook(res.data);
      });
    }
  };

  const handleFavoriteClick = () => {
    if (!book) return;

    if (book.isFavorite) {
      BookService.deleteFavorite(book.id).then(() => {
        fetchBookDetail();
      });
    } else {
      BookService.createFavorite(book.id).then(() => {
        fetchBookDetail();
      });
    }
  };

  const refreshUserInfo = () => {
    UserService.getInfo(abortController.signal).then((res) => {
      StorageService.setObject(USER_INFO_KEY, res.data);
      dispatch(storeUser(res.data));
    });
  };

  const handleUpgradeFlow = (mediaType?: BookMediaType) => {
    if (!userInfo.id) {
      navigate(RoutePath.HOME);

      return;
    }

    if (!book) {
      return;
    }

    if (book.bookOption === BookOption.VIP && !book.price) {
      navigate(RoutePath.UPGRADE);

      return;
    }

    const query = mediaType ? `&media_type=${mediaType}` : "";

    navigate(`${RoutePath.UPGRADE}?book_id=${book.id}${query}`);
  };

  const handleListenBookClick = () => {
    if (!book?.audio?.enabled) {
      return;
    }

    if (!book.audio.hasAccess) {
      if (book.audio.canPurchase) {
        handlePurchaseMediaClick(BookMediaType.AUDIO);

        return;
      }

      handleUpgradeFlow(BookMediaType.AUDIO);

      return;
    }

    if (audiobookState.audiobookData.bookId === book.id) {
      return;
    }

    const audiobookData: AudiobookData = {
      bookId: book.id,
      bookName: book.name,
      author: getDisplayAuthor(book),
      thumbnail: book.thumbnails[0],
      audioList:
        book.elements
          ?.filter((item) => !!getElementAudioLink(item))
          ?.map((item) => ({
            id: item.id,
            name: item.name,
            link: getElementAudioLink(item),
          })) ?? [],
    };

    dispatch(showAudiobookPanel());
    dispatch(sendAudiobook(audiobookData));
  };

  const handleReadBookClick = () => {
    if (!book?.ebook?.enabled) {
      dispatch(clearAudiobook());
      navigate(`${RoutePath.READING}/${book?.id}`);

      return;
    }

    if (!book.ebook.hasAccess) {
      if (book.ebook.canPurchase) {
        handlePurchaseMediaClick(BookMediaType.EBOOK);

        return;
      }

      handleUpgradeFlow(BookMediaType.EBOOK);

      return;
    }

    dispatch(clearAudiobook());
    navigate(`${RoutePath.EBOOK_READING}/${book.id}`);
  };

  const handlePurchaseMediaClick = (mediaType: BookMediaType) => {
    if (!book || !userInfo.id) {
      navigate(RoutePath.HOME);

      return;
    }

    const isEbook = mediaType === BookMediaType.EBOOK;
    const requiredKas = isEbook ? Number(book.ebook?.price ?? 0) : Number(book.audio?.price ?? 0);
    const currentKas = Number(userInfo.point ?? 0);
    const purchaseConfirmModalObs = openPortalDialog(PurchaseMediaConfirmModal, {
      bookName: book.name,
      mediaLabel: isEbook ? "Ebook" : "Audio",
      currentKas,
      requiredKas,
    });

    purchaseConfirmModalObs.afterClosed().subscribe((data) => {
      if (!data?.isAccept) {
        return;
      }

      if (!data.isEnoughKas) {
        navigate(RoutePath.VAS_OPTION_LIST);

        return;
      }

      PurchaseService.purchaseBookByMediaType(book.id, mediaType).then(() => {
        addToast({
          text: isEbook ? "Mua ebook thành công" : "Mua audio thành công",
        });
        refreshUserInfo();
        fetchBookDetail();
      });
    });
  };

  const handleReadTextBookClick = () => {
    if (!book) {
      return;
    }

    dispatch(clearAudiobook());
    navigate(`${RoutePath.READING}/${book.id}`);
  };

  const handleBuyOrUpdateVipBookClick = (mediaType?: BookMediaType) => {
    handleUpgradeFlow(mediaType);
  };

  const handleGotoBuyBookLinkClick = () => {
    if (!book?.linkStore) {
      addToast({ text: SystemMessage.LINK_STORE_EMPTY, status: "warn" });

      return;
    }

    window.open(book.linkStore);
  };

  const handleTryListenClick = () => {
    if (!book) return;

    if (audiobookState.audiobookData.bookId === book.id) {
      return;
    }

    const audioList: AudioItem[] = [];

    for (let i = 0; i < book.elements.length; i++) {
      const current = book.elements[i];
      const audioLink = getElementAudioLink(current);

      if (current.id && current.name && audioLink) {
        audioList.push({
          id: current.id,
          name: current.name,
          link: audioLink,
        });
        break;
      }
    }

    if (!audioList.length) return;

    const audiobookData: AudiobookData = {
      bookId: book.id,
      bookName: book.name,
      author: getDisplayAuthor(book),
      thumbnail: book.thumbnails[0],
      audioList,
    };

    dispatch(showAudiobookPanel());
    dispatch(sendAudiobook(audiobookData));
  };

  const handleDiscoveryClick = () => {
    navigate(RoutePath.BOOK_CATEGORY_FAVORITE);
  };

  const handleUnLockBookElement = (bookElement: BookElementTreeItem) => {
    const unlockBookElementModalObs = openPortalDialog(UnlockBookElementModal, {
      bookElement: bookElement as BookElementDetail,
      isEnoughPoint: userInfo.point >= (bookElement.point ?? 0),
    });

    unlockBookElementModalObs.afterClosed().subscribe((data) => {
      if (data?.status === "success") {
        fetchBookDetail();
        UserService.getInfo(abortController.signal).then((res) => {
          StorageService.setObject(USER_INFO_KEY, res.data);
          dispatch(storeUser(res.data));
        });
      }
    });
  };

  const handleReadOrUnlockBookElementClick = (
    book: BookDetail,
    bookElement: BookElementTreeItem
  ) => {
    const chapterIndex = flattenedBookElements.findIndex(
      (item) => item.id === bookElement.id
    );

    if (bookElement.order === 1 && !book.isAvailable && book.isTryListening) {
      navigate(`${RoutePath.READING}/${book.id}`);

      return;
    }

    if (bookElement.isLock) {
      handleUnLockBookElement(bookElement);
    } else {
      const chapterParam =
        chapterIndex >= 0 ? chapterIndex : Math.max(0, bookElement.order - 1);

      navigate(
        `${RoutePath.READING}/${book.id}?chapter=${chapterParam}`
      );
    }
  };

  const flattenedBookElements = useMemo(
    () => flattenBookElements(book?.elements),
    [book?.elements]
  );
  const authorItems = useMemo(() => {
    if (book?.authors?.length) {
      return book.authors
        .filter((item) => !!item.fullName)
        .map((item) => ({
          id: item.id,
          name: item.fullName,
        }));
    }

    if (book?.author) {
      return [
        {
          id: null,
          name: book.author,
        },
      ];
    }

    return [];
  }, [book?.authors, book?.author]);
  const categoryItems = useMemo(
    () =>
      (book?.categories ?? [])
        .filter((item) => !!item.categoryName)
        .map((item) => ({
          id: item.categoryId,
          name: item.categoryName,
        })),
    [book?.categories]
  );

  const isCanUnlockOrRead = useCallback(
    (bookElement: BookElementTreeItem) => {
      if (bookElement.order <= 2) return true;

      const prevElement = flattenedBookElements.find(
        (item) => item.order === bookElement.order - 1
      );

      return !!prevElement && !prevElement.isLock;
    },
    [flattenedBookElements]
  );

  const isCanListenSample = useMemo(() => {
    return !!book && !!book.audio?.enabled && !book?.isAvailable && book.isTryListening;
  }, [book]);

  const hasAudioAccess = Boolean(book?.audio?.enabled && book.audio.hasAccess);
  const hasEbookAccess = Boolean(book?.ebook?.enabled && book.ebook.hasAccess);

  const renderBookElementTableOfContent = (
    bookData: BookDetail,
    item: BookElementTreeItem,
    depth = 0
  ) => {
    const children = getElementChildren(item);
    const hasChildren = children.length > 0;
    const canRead = isCanUnlockOrRead(item);

    return (
      <div key={item.id}>
        <div
          className={clsx(
            "flex justify-between items-center",
            canRead
              ? "cursor-pointer hover:opacity-80"
              : "pointer-events-none opacity-60"
          )}
          style={{ marginLeft: depth * 20 }}
          onClick={() => handleReadOrUnlockBookElementClick(bookData, item)}
        >
          <div className="text-[#3A4251] font-medium font-[SVN-Gilroy]">
            {item.order}. {item.name}
            {hasChildren && (
              <img
                className="inline ml-2"
                src={Images.ArrowDown.default}
                alt=""
                onClick={(event) => {
                  event.stopPropagation();
                  toggleSubmenu(item.id);
                }}
              />
            )}
          </div>

          {item.order === 1 && isCanListenSample ? (
            <button className="!rounded-[12px] !w-[80px] !h-[36px] !text-[12px] font-bold !bg-gradient-to-b from-[#8092e4] to-[#38499b] text-white flex justify-center items-center">
              Nghe thử
            </button>
          ) : item.isLock ? (
            <LuLockKeyhole />
          ) : (
            <LuLockKeyholeOpen />
          )}
        </div>

        {hasChildren && !!openSubmenus[item.id] && (
          <div className="mt-2 flex flex-col gap-2">
            {children.map((child) =>
              renderBookElementTableOfContent(bookData, child, depth + 1)
            )}
          </div>
        )}
      </div>
    );
  };

  const handleShareClick = () => {
    if (!book) return;

    DeepLinkService.createLink(book.id).then((res) => {
      copy(res.data.shortLink).then(() => {
        addToast({ text: SystemMessage.COPY_SUCCESSFULLY });
      });
    });
  };

  const handleAuthorClick = (authorId?: number | null) => {
    if (!authorId) return;

    navigate(`${RoutePath.AUTHOR}/${authorId}`);
  };

  const handleCategoryClick = (categoryId?: number | null) => {
    if (!categoryId) return;

    navigate(`${RoutePath.BOOK_BY_CATEGORY}?category_id=${categoryId}`);
  };

  return (
    <div className="px-[100px]">
      <div className="text-sm font-medium flex align-middle h-5 mt-10 gap-1">
        <div className="font-[SVN-Gilroy] text-[#3A4251]">Trang chủ</div>
        <img src={Images.ArrowRightBlack.default} width={16} alt="" />
        {!loading ? (
          <div className="font-[SVN-Gilroy] text-[#3A4251]">{book?.name}</div>
        ) : (
          <Skeleton className="w-28" />
        )}
      </div>
      <div className="mt-8 flex justify-between">
        <div className="w-[37%]">
          {!loading && !!book ? (
            <ImageGallery
              items={book.thumbnails?.map((item) => ({ original: item })) ?? []}
              showPlayButton={false}
              showIndex
              autoPlay
            />
          ) : (
            <Skeleton className="w-full h-[595px]" />
          )}
        </div>
        <div className="w-[59%]">
          <div className="flex justify-between gap-3">
            <div className="text-2xl font-bold font-[SVN-Gilroy]">
              {!loading ? book?.name : <Skeleton className="w-52 h-8" />}
            </div>
            {!loading ? (
              <div className="flex items-center gap-5">
                <HeartIcon
                  isLiked={book?.isFavorite}
                  onClick={handleFavoriteClick}
                />
                <img
                  className="size-8 cursor-pointer"
                  src={Images.ShareBook.default}
                  alt=""
                  onClick={handleShareClick}
                />
              </div>
            ) : (
              <Skeleton className="w-8 h-8 rounded-full" />
            )}
          </div>
          <div className="text-lg font-[SVN-Gilroy] mt-2 text-[#4E586C] font-medium">
            {!loading ? (
              <div className="flex flex-wrap items-center gap-y-1">
                {authorItems.map((item, index) => (
                  <div key={`author-${item.id ?? item.name}`} className="flex items-center">
                    <span
                      className={clsx({
                        "cursor-pointer hover:text-[#38499b]": !!item.id,
                      })}
                      onClick={() => handleAuthorClick(item.id)}
                    >
                      {item.name}
                    </span>
                    {index < authorItems.length - 1 && (
                      <span className="mx-2">|</span>
                    )}
                  </div>
                ))}
                {!!authorItems.length && !!categoryItems.length && (
                  <span className="mx-2">|</span>
                )}
                {categoryItems.map((item, index) => (
                  <div key={`category-${item.id}`} className="flex items-center">
                    <span
                      className="cursor-pointer hover:text-[#38499b]"
                      onClick={() => handleCategoryClick(item.id)}
                    >
                      {item.name}
                    </span>
                    {index < categoryItems.length - 1 && (
                      <span className="mx-2">|</span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <Skeleton className="w-52 h-7" />
            )}
          </div>
          {!loading && !!book ? (
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <span
                className={clsx(
                  "p-2 rounded-lg text-sm font-medium font-[SVN-Gilroy] tracking-[0.028px]",
                  {
                    "bg-[#E6FFED] text-[#00A653]":
                      book.bookOption === BookOption.FREE,
                    "bg-[#FFFEE6] text-[#FFD500]":
                      book.bookOption === BookOption.VIP,
                    "bg-[#FFE4D6] text-[#FF7029]":
                      book.bookOption === BookOption.PURCHASE,
                  }
                )}
              >
                {book.bookOption === BookOption.PURCHASE ? (
                  <span>
                    {formatMoneyVN(book.price)}
                    <span className="underline">đ</span>
                  </span>
                ) : (
                  BookOptionName[book.bookOption]
                )}
              </span>
              {book.bookOption === BookOption.PURCHASE && !!book.audio?.enabled && (
                <span className="rounded-lg bg-[#EEF2FF] px-3 py-2 text-sm font-semibold text-[#38499B]">
                  Audio: {formatMoneyVN(book.audio.price)}đ
                </span>
              )}
              {book.bookOption === BookOption.PURCHASE && !!book.ebook?.enabled && (
                <span className="rounded-lg bg-[#ECFDF3] px-3 py-2 text-sm font-semibold text-[#288564]">
                  Ebook: {formatMoneyVN(book.ebook.price)}đ
                </span>
              )}
            </div>
          ) : (
            <Skeleton className="mt-4 w-20 h-[35px] rounded-lg" />
          )}
          <div className="mt-[56px] flex flex-wrap gap-5">
            {!!book?.audio?.enabled && !book?.ebook?.enabled && hasAudioAccess && (
              <button
                className="!rounded-3xl !w-[192px] !h-12 font-bold !bg-gradient-to-b from-[#8092e4] to-[#38499b] text-white flex justify-center items-center"
                onClick={handleReadTextBookClick}
              >
                <img className="pr-2" src={Images.Text.default} alt="" />
                Đọc sách
              </button>
            )}
            {!!book?.ebook?.enabled && (
              <button
                className="!rounded-3xl !w-[192px] !h-12 font-bold !bg-gradient-to-b from-[#4BC282] to-[#288564] text-white flex justify-center items-center"
                onClick={handleReadBookClick}
              >
                <img className="pr-2" src={Images.Text.default} alt="" />
                {hasEbookAccess
                  ? "Đọc ebook"
                  : book.ebook?.canPurchase
                    ? "Mua ebook"
                    : "Mở ebook"}
              </button>
            )}
            {!!book?.audio?.enabled && (
              <button
                className="!rounded-3xl !w-[192px] !h-12 font-bold !bg-gradient-to-b from-[#8092e4] to-[#38499b] text-white flex justify-center items-center"
                onClick={handleListenBookClick}
              >
                <img className="pr-2" src={Images.HeadPhone.default} alt="" />
                {hasAudioAccess
                  ? "Nghe sách"
                  : book.audio?.canPurchase
                    ? "Mua audio"
                    : "Mở audio"}
              </button>
            )}
            {!!book && !hasAudioAccess && !hasEbookAccess && (
              <>
                {((book.bookOption === BookOption.VIP && book.price > 0) ||
                  book.bookOption === BookOption.PURCHASE) && (
                  <button
                    className="!rounded-3xl !w-[192px] !h-12 font-bold !bg-gradient-to-b from-[#8092e4] to-[#38499b] text-white flex justify-center items-center"
                    onClick={() => handleBuyOrUpdateVipBookClick()}
                  >
                    <img
                      className="pr-2"
                      src={Images.ShoppingCartIcon.default}
                      alt=""
                    />
                      Mua Sách
                  </button>
                )}
                {book.bookOption === BookOption.VIP && book.price === 0 && (
                  <button
                    className="!rounded-3xl !w-[192px] !h-12 font-bold !bg-gradient-to-b from-[#8092e4] to-[#38499b] text-white flex justify-center items-center gap-1"
                    onClick={() => handleBuyOrUpdateVipBookClick()}
                  >
                    <img
                      className="w-[16px] h-[16px]"
                      src={Images.CrownIcon.default}
                      alt=""
                    />
                    Nâng cấp VIP
                  </button>
                )}
              </>
            )}
            {!!book && !book.available && book.isTryListening && (
              <button
                className="!rounded-3xl !w-[192px] !h-12 font-bold !bg-gradient-to-b from-[#8092e4] to-[#38499b] text-white flex justify-center items-center"
                onClick={handleTryListenClick}
              >
                <img
                  className="pr-2"
                  src={Images.HeadPhoneRoundIcon.default}
                  alt=""
                />
                Nghe thử
              </button>
            )}
          </div>
          <div className="mt-[30px]">
            {!!book?.linkStore && book.linkStore.startsWith("http") && (
              <button
                className="!rounded-3xl !w-[280px] !h-[40px] font-semibold text-[14px] text-[#333B47] border-[1px] border-[#FFD500] flex justify-center items-center gap-[6px]"
                onClick={handleGotoBuyBookLinkClick}
              >
                Bạn có thể mua sách giấy tại đây
                <FaLongArrowAltRight />
              </button>
            )}
          </div>
          <div>
            <div className="text-xl font-semibold mt-[56px] font-[SVN-Gilroy] text-[#171A20]">
              Giới thiệu nội dung
            </div>
            {!loading ? (
              <div className="text-center mt-4">
                {expanded ? (
                  <div className="text-justify font-[SVN-Gilroy] text-[#4E586C] whitespace-pre-line">
                    {book?.description}
                  </div>
                ) : (
                  <div className="text-justify font-[SVN-Gilroy] text-[#4E586C] whitespace-pre-line">
                    {book?.description?.slice(0, 1207)}
                    {!!book?.description?.length &&
                      book?.description.length > 1207 &&
                      "..."}
                  </div>
                )}
                {!!book?.description?.length &&
                  book?.description.length > 1207 && (
                    <button
                      className="text-[#38499b] mt-2 font-bold font-[SVN-Gilroy] text-sm"
                      onClick={toggleExpand}
                    >
                      {expanded ? "Ẩn bớt" : "Xem thêm"}
                    </button>
                  )}
              </div>
            ) : (
              <Skeleton className="w-96 h-6 mt-4" />
            )}
          </div>
          <div>
            <div className="text-lg font-bold font-[SVN-Gilroy] mt-11">
              Mục lục
            </div>
            {!loading ? (
              <div className="mt-5 flex flex-col gap-3">
                {!!book?.elements?.length &&
                  book.elements.map((item) =>
                    renderBookElementTableOfContent(book, item)
                  )}
              </div>
            ) : (
              <Skeleton className="w-96 h-6 mt-5" />
            )}
          </div>
        </div>
      </div>
      <div>
        <div className="flex justify-between items-center my-10">
          <div className="text-[#171A20] font-bold text-xl font-[SVN-Gilroy]">
            Có Thể Bạn Thích
          </div>
          <div
            className="text-[#38499b] text-sm font-semibold cursor-pointer flex items-center"
            onClick={handleDiscoveryClick}
          >
            Khám phá
            <img
              className="inline ml-2"
              src={Images.ArrowRight.default}
              alt=""
            />
          </div>
        </div>
        <div className="mt-2">
          {!loadingRecommendations ? (
            <BookGrid books={bookCategoryFavorite} />
          ) : (
            <div className="flex gap-8 flex-wrap">
              <div>
                <Skeleton className="w-[200px] h-[300px] rounded-lg" />
                <Skeleton className="w-[200px] mt-1 h-7" />
              </div>
              <div>
                <Skeleton className="w-[200px] h-[300px] rounded-lg" />
                <Skeleton className="w-[200px] mt-1 h-7" />
              </div>
              <div>
                <Skeleton className="w-[200px] h-[300px] rounded-lg" />
                <Skeleton className="w-[200px] mt-1 h-7" />
              </div>
              <div>
                <Skeleton className="w-[200px] h-[300px] rounded-lg" />
                <Skeleton className="w-[200px] mt-1 h-7" />
              </div>
              <div>
                <Skeleton className="w-[200px] h-[300px] rounded-lg" />
                <Skeleton className="w-[200px] mt-1 h-7" />
              </div>
              <div>
                <Skeleton className="w-[200px] h-[300px] rounded-lg" />
                <Skeleton className="w-[200px] mt-1 h-7" />
              </div>
              <div>
                <Skeleton className="w-[200px] h-[300px] rounded-lg" />
                <Skeleton className="w-[200px] mt-1 h-7" />
              </div>
            </div>
          )}
        </div>
        <BannerAdsComponent
          className="mt-8"
          position={BannerAdsPosition.BOOK_DETAIL_SCREEN}
        />
      </div>
    </div>
  );
}

export default BookDetailPage;
